/**
 * D331: the Decorations sheet, opened from the button on the map beside Photo. It sits under
 * the map, which stays in view: tapping a decoration tries it on the player's own world, in
 * either renderer, before it is bought. Decorations cost Trimmings; one not yet open is greyed
 * with the festivals it waits for. Nothing is stored until Buy or Show is pressed.
 */
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { IdleDecorationKind } from '@hedgerow/data';
import { idleTrimmings } from '@hedgerow/sim';
import { flyCoins, playSound, popNumber } from '../juice/index.ts';
import { Button } from '../ui/Button.tsx';
import { SheetBack, SheetClose } from '../ui/Sheet.tsx';
import { num } from './notation.ts';
import { idleContent, useIdle } from './store.ts';
import { t as term } from './terms.ts';
import { decorationGroups, refusalText, type DecorationOption } from './view.ts';

const SMALL: JSX.CSSProperties = {
  font: '400 var(--fs-label)/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
};

/** What an option's second line says: on the world, owned, its price, or why it waits. */
function statusOf(o: DecorationOption): string {
  if (o.selected) return `On the ${term('world')}`;
  if (o.owned) return 'Owned';
  if (o.locked) return o.locked;
  return `${num(o.cost)} ${term('trimmings')}${o.thisWeek ? ', this week only' : ''}`;
}

function OptionCard({ o, onTry }: { o: DecorationOption; onTry: () => void }) {
  return (
    <button
      type="button"
      data-testid={`decoration-${o.id}`}
      aria-pressed={o.trying}
      onClick={onTry}
      style={{
        display: 'grid',
        gap: 2,
        minHeight: 52,
        padding: '6px 10px',
        textAlign: 'left',
        borderRadius: 'var(--radius-md)',
        background: o.trying ? 'var(--surface-2)' : 'var(--surface)',
        border: `${o.trying ? 2 : 1}px solid ${o.trying ? 'var(--accent)' : 'var(--line)'}`,
        color: 'var(--ink)',
        opacity: o.locked ? 0.55 : 1,
        cursor: 'pointer',
      }}
    >
      <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>{o.name}</span>
      <span
        style={{
          ...SMALL,
          fontSize: 'var(--fs-caption)',
          ...(o.trying ? { color: 'var(--ink)' } : {}),
        }}
      >
        {statusOf(o)}
      </span>
    </button>
  );
}

/** The action for the option being tried on: buy it, show it, or say why not. */
function TryBar({ o }: { o: DecorationOption }) {
  const act = (e: MouseEvent, buy: boolean) => {
    const el = e.currentTarget as Element;
    const r = useIdle
      .getState()
      .act(
        buy
          ? { type: 'buyDecoration', decoration: o.id }
          : { type: 'setDecoration', decoration: o.id },
      );
    if (!r.ok) {
      playSound('error');
      popNumber(el, refusalText(idleContent, r));
      return;
    }
    playSound(buy ? 'buy' : 'tap');
    if (buy) {
      popNumber(el, 'Bought');
      flyCoins(el, { count: 4 });
    }
    useIdle.getState().previewDecoration(o.kind, null);
  };
  const btn = { padding: '0 14px', font: '600 var(--fs-body)/1 var(--font-ui)' };
  return (
    <div
      data-testid="decoration-try"
      style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}
    >
      <span style={{ font: '400 var(--fs-body)/1.35 var(--font-ui)' }}>
        Trying <b>{o.name}</b> on the map
      </span>
      {o.selected ? null : o.owned ? (
        <Button style={btn} onClick={(e) => act(e, false)}>
          Show
        </Button>
      ) : (
        <Button
          style={btn}
          {...(o.locked
            ? { reason: o.locked }
            : o.needs !== undefined
              ? { reason: `Needs ${num(o.needs)} ${term('trimmings')}` }
              : {})}
          onClick={(e) => act(e, true)}
        >
          Buy, <span class="num">{num(o.cost)}</span> {term('trimmings')}
        </Button>
      )}
    </div>
  );
}

export function DecorationsSheet() {
  const world = useIdle((s) => s.world);
  const now = useIdle((s) => s.now);
  const trying = useIdle((s) => s.decorPreview);
  const [kind, setKind] = useState<IdleDecorationKind>('building');
  if (!world) return null;
  const groups = decorationGroups(idleContent, world, now, trying);
  const group = groups.find((g) => g.kind === kind) ?? groups[0]!;
  const tried = group.options.find((o) => o.trying);
  const pick = (k: IdleDecorationKind) => {
    // One try at a time: the map shows the world as it is plus the one being looked at.
    for (const g of groups) useIdle.getState().previewDecoration(g.kind, null);
    setKind(k);
  };
  const tryOn = (o: DecorationOption) => {
    const store = useIdle.getState();
    store.previewDecoration(o.kind, o.trying || o.selected ? null : o.id);
  };
  return (
    <div
      data-testid="decorations"
      style={{
        // Section 11: over everything, the tab bar included. The map above stays in view and
        // tappable, so a decoration is still tried on before it is bought (D331).
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 40,
        maxHeight: '55%',
        boxSizing: 'border-box',
        overflowY: 'auto',
        display: 'grid',
        gap: 8,
        alignContent: 'start',
        padding: '8px 12px calc(12px + env(safe-area-inset-bottom))',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.12)',
        background: 'var(--bg)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: 'var(--line)',
          margin: '0 auto',
        }}
      />
      {/* D458: opened from The farm sheet, so Back as well as Close; no Done. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 28 }}>
        <SheetBack
          onBack={() => {
            const store = useIdle.getState();
            store.setDecorOpen(false);
            store.setFarmOpen(true);
          }}
        />
        <h2 style={{ margin: 0, flex: 1, font: '600 var(--fs-title)/1.3 var(--font-ui)' }}>
          Decorate
        </h2>
        <span
          data-testid="trimmings"
          style={{
            font: '600 var(--fs-body)/1 var(--font-ui)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {num(idleTrimmings(world))} {term('trimmings')}
        </span>
        <SheetClose onClose={() => useIdle.getState().setDecorOpen(false)} />
      </div>
      <span style={SMALL}>
        They change the look of the {term('world')}, never the numbers. Tap one to try it on. Earn{' '}
        {term('trimmings')} from the daily gift, orders, visitors,{' '}
        {term('luckyFinds').toLowerCase()} and the weekly event.
      </span>
      {/* A scroll box may shrink to nothing in a grid row; the chips keep their 44px. */}
      <div
        role="tablist"
        style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, minHeight: 48 }}
      >
        {groups.map((g) => (
          <button
            key={g.kind}
            type="button"
            role="tab"
            aria-selected={g.kind === group.kind}
            data-testid={`decorations-${g.kind}`}
            onClick={() => pick(g.kind)}
            style={{
              flex: '0 0 auto',
              minHeight: 44,
              padding: '0 12px',
              borderRadius: 999,
              border: '1px solid var(--line)',
              background: g.kind === group.kind ? 'var(--accent)' : 'var(--surface)',
              color: g.kind === group.kind ? 'var(--accent-ink)' : 'var(--ink)',
              font: '600 var(--fs-label)/1 var(--font-ui)',
              cursor: 'pointer',
            }}
          >
            {g.label}
          </button>
        ))}
      </div>
      {tried && <TryBar o={tried} />}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 6,
        }}
      >
        {group.options.map((o) => (
          <OptionCard key={o.id} o={o} onTry={() => tryOn(o)} />
        ))}
      </div>
    </div>
  );
}
